import { Component, EventListener, findComponents, startComponents, stopComponents } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { ListFilter } from '@swisslife/patterns-base/src/molecules/list-filter/js/list-filter';
import '@swisslife/patterns-base/src/molecules/list-filter/js/list-filter';
import '../../../molecules/teaser-row/js/teaser-row';
import ajax from '@swisslife/shared-ui/src/utils/ajax/js/ajax';
import { dispatchCustomEvent, EventNames } from '@swisslife/shared-ui/src/utils/custom-event/js/custom-event';
import addComponentAnimation from '@swisslife/shared-ui/src/utils/animations/js/animations';

export interface IConfiguration {
	serviceUri: string;
	serviceUriSuffix: string;
	pagePrefix: string;
	categoryPrefix: string;
	subCategoryPrefix: string;
	subCategoryDelimiter: string;
}

enum States {
	LoaderVisible = 'state-a-loader--visible',
	Error = 'state-o-purpose-hub--error',
	PreInit = 'state-o-purpose-hub--pre-init',
}

enum Selectors {
	Loader = '.js-a-loader',
	PurposeHubTeaser = '.js-o-purpose-hub__teaser',
	ListOutlet = '.js-o-purpose-hub__outlet',
	ResultTitle = '.js-o-purpose-hub__result-title',
	ResultCount = '.js-o-purpose-hub__result-count',
	LoadMore = '.js-o-purpose-hub__load-more',
	LoadMoreButton = '.js-o-purpose-hub__load-more-button',
	Fragment = '.js-o-purpose-hub__fragment',
}

enum Actions {
	Append = 'append',
	Replace = 'replace',
}

// The @Component decorator will connect the class with `data-g-name="PurposeHub"` elements.
@Component('PurposeHub')
export class PurposeHub extends GondelJqueryComponent {
	private $loader: JQuery<HTMLElement>;
	private $listOutlet: JQuery<HTMLElement>;
	private $resultTitle: JQuery<HTMLElement>;
	private $resultCount: JQuery<HTMLElement>;
	private $purposeHubTeaser: JQuery<HTMLElement>;
	private $loadMore: JQuery<HTMLElement>;
	private listFilter: ListFilter;
	private configuration: IConfiguration;
	private defaultCategory: { itemKey: string; itemName: string } = { itemKey: '', itemName: '' };
	private filteredCategory: { itemKey: string; itemName: string } = { itemKey: '', itemName: '' };
	private selectedCategory: { itemKey: string; itemName: string } = { itemKey: '', itemName: '' };
	private selectedSubCategories: string[] = [];
	private currentPage: number = 0;
	private resultCount: number = 0;
	private hasLoadMore: boolean = false;
	private subCategoryAllKeyName: string = 'all';
	private controllers: any[] = Array();

	public async start() {
		this.controllers = await addComponentAnimation(this.$ctx);
		// add list-filter dependencies and register callback
		const listFilterComponents = findComponents<ListFilter>(this._ctx, 'ListFilter');

		if (listFilterComponents.length > 0) {
			this.listFilter = listFilterComponents[0];
			this.listFilter.registerCallback(this.listFilterClickCallback);
		}

		this.$loader = $(Selectors.Loader, this.$ctx);
		this.$listOutlet = $(Selectors.ListOutlet, this.$ctx);
		this.$purposeHubTeaser = $(Selectors.PurposeHubTeaser, this.$ctx);
		this.$resultTitle = $(Selectors.ResultTitle, this.$ctx);
		this.$resultCount = $(Selectors.ResultCount, this.$ctx);
		this.$loadMore = $(Selectors.LoadMore, this.$ctx);

		this.configuration = {
			serviceUri: this.$listOutlet.data('service-uri'),
			serviceUriSuffix: this.$listOutlet.data('service-uri-suffix') || '',
			pagePrefix: this.$listOutlet.data('page-prefix'),
			categoryPrefix: this.$listOutlet.data('category-prefix'),
			subCategoryPrefix: this.$listOutlet.data('sub-category-prefix'),
			subCategoryDelimiter: this.$listOutlet.data('sub-category-delimiter') || '|',
		};

		this.currentPage = this.$listOutlet.data('current-page');
		this.resultCount = this.$listOutlet.data('result-count');
		this.hasLoadMore = this.$ctx.data('has-load-more') || false;

		if (this.listFilter) {
			$(window).on('popstate.purposeHub', this.handleHistoryPopState.bind(this));
		}
	}

	public sync() {
		if (this.listFilter) {
			this.defaultCategory = this.listFilter.getDefaultCategoryKey();
			this.filteredCategory = this.listFilter.getFilteredCategoryKey();
			if (this.filteredCategory.itemKey) {
				// set initial filter state if category (and subcategories) are preselected
				this.selectedCategory = this.filteredCategory;
				let initialSubCategories: string[] = [];
				const initialSubCategoriesData = this.$ctx.data('initial-subcategories');
				if (initialSubCategoriesData) {
					initialSubCategories = initialSubCategoriesData.split(this.configuration.subCategoryDelimiter);
				}
				this.listFilter.setFilterState(this.selectedCategory.itemKey, initialSubCategories);
				$.each(initialSubCategories, (index, selectedSubCategory) => {
					this.updateSelectedSubCategory(selectedSubCategory, true);
				});
			} else {
				this.selectedCategory = this.defaultCategory;
			}
		}

		this.setResultCount(this.resultCount);
		this.toggleLoadMore(this.hasLoadMore);
		this.$ctx.removeClass(States.PreInit);
	}

	public stop() {
		for (const controller of this.controllers) {
			controller.destroy(true);
		}
		if (this.listFilter) {
			this.listFilter.removeCallback(this.listFilterClickCallback);
		}

		$(window).off('popstate.purposeHub');
	}

	@EventListener('click', Selectors.LoadMoreButton)
	public _handleLoadMoreButtonClick(): void {
		this.currentPage++;
		this.getResults(Actions.Append, true);
	}

	private generateRequestUrl(): string {
		const urlArray: string[] = [];

		urlArray.push(this.configuration.serviceUri);
		urlArray.push(`${this.configuration.pagePrefix}${this.currentPage}`);
		urlArray.push(`${this.configuration.categoryPrefix}${this.selectedCategory.itemKey}`);

		if (this.selectedSubCategories.length > 0) {
			const selectedSubCategoriesSorted = this.selectedSubCategories.sort();
			urlArray.push(
				`${this.configuration.subCategoryPrefix}${selectedSubCategoriesSorted.join(
					this.configuration.subCategoryDelimiter
				)}`
			);
		}

		urlArray.push(this.configuration.serviceUriSuffix);

		return urlArray.join('.');
	}

	private generateUrlFragmentFilterState(): string {
		const urlArray: string[] = [];

		urlArray.push(`${this.configuration.pagePrefix}${this.currentPage}`); // e.g. 'page-0'
		urlArray.push(`${this.configuration.categoryPrefix}${this.selectedCategory.itemKey}`); // e.g. 'cat-ch__private'

		if (this.selectedSubCategories.length > 0) {
			const selectedSubCategoriesSorted = this.selectedSubCategories.sort();

			urlArray.push(
				`${this.configuration.subCategoryPrefix}${selectedSubCategoriesSorted.join(
					this.configuration.subCategoryDelimiter
				)}`
			); // e.g. 'subcat-ch__service--karriere|ch__service--kontakt|ch__service--vertriebspartner'
		}

		urlArray.push(this.configuration.serviceUriSuffix);

		return urlArray.join('.');
	}

	private generateUnfilteredUrl(): string {
		const urlPathParts = document.location.href.split('/');
		const lastSegment = urlPathParts.pop();
		const restSegment = urlPathParts.join('/');
		let urlFragmentBase = '';

		if (typeof lastSegment !== 'undefined') {
			/* this separating string occurs once in agreed url pattern ->
			 * 1st part contains page name, 2nd the filter state (with paging and serviceUriSuffix)
			 */
			const partsLastSegment = lastSegment.split(`.${this.configuration.pagePrefix}`);
			const partsLastSegmentBase = partsLastSegment[0];

			if (typeof partsLastSegmentBase !== 'undefined') {
				const partsLastSegmentWithoutSuffix = partsLastSegmentBase.replace(
					`.${this.configuration.serviceUriSuffix}`,
					''
				); // strip out serviceUriSuffix
				urlFragmentBase = partsLastSegmentWithoutSuffix;
			}
		}

		return `${restSegment}/${urlFragmentBase}`;
	}

	private async getResults(action: Actions, updateHistory: boolean) {
		try {
			this.toggleLoadingState(true);

			// fetch but wait for 150 ms minimum (prevent too fast update / ui flickering)
			const fragment = (await Promise.all([ajax<string>(this.generateRequestUrl()), this.wait(150)]))[0];
			this.parseResult(fragment);

			// reflect filter state in url
			const urlFragmentFilterState = this.generateUrlFragmentFilterState(); // e.g. 'page-0.cat-ch__people.html'
			const urlFragmentUnfiltered = this.generateUnfilteredUrl();

			if (updateHistory) {
				this.updateHistory(document.title, `${urlFragmentUnfiltered}.${urlFragmentFilterState}`);
			}

			if (action === Actions.Replace) {
				stopComponents(this.$listOutlet);
				this.$listOutlet.empty();
			}

			this.$ctx.removeClass(States.Error);
			this.$listOutlet.append(fragment);
			startComponents(this.$listOutlet.find(`${Selectors.Fragment}:last-child`));
		} catch (error) {
			this.resultCount = 0;
			this.$listOutlet.empty();
			this.hasLoadMore = false;
			const statusCode = error.status ? error.status.toString() : 'n/a';
			dispatchCustomEvent(this.$ctx, EventNames.NetworkError, { statusCode });
			this.showErrorMessage();
		}

		// title and count only changes if there is a list filter component
		if (this.listFilter) {
			const showSubcatAsResultTitle = this.selectedSubCategories.indexOf(this.subCategoryAllKeyName) !== -1;

			// special treatment for 'all' subcat filter: set subcat text instead of selectedCategory
			if (showSubcatAsResultTitle) {
				this.setResultTitle(this.listFilter.getSubCategoryAllText());
			} else {
				this.setResultTitle(this.selectedCategory.itemName);
			}
			this.setResultCount(this.resultCount);
		}

		// check visibility of the manual purpose hub teaser
		this.togglePurposeHubTeaser();
		this.toggleLoadingState(false);
		this.toggleLoadMore(this.hasLoadMore);
	}

	private handleHistoryPopState(event): void {
		// get original event since jQuery normalizes the event
		const popStateEvent = event.originalEvent;

		if (popStateEvent.state !== null) {
			const eventData = popStateEvent.state;

			this.selectedCategory = eventData.selectedCategory;
			this.selectedSubCategories = eventData.selectedSubCategories;
			this.resultCount = eventData.resultCount;
			this.currentPage = eventData.currentPage;

			this.listFilter.setFilterState(this.selectedCategory.itemKey, this.selectedSubCategories);

			// history popState is always Actions.replace because we do push load more as a new history event
			this.getResults(Actions.Replace, false);
		} else {
			window.location.reload();
		}
	}

	private listFilterClickCallback = (
		filterType: string,
		filterItem: { itemKey: string; itemName: string },
		isSelected: boolean
	): void => {
		let analyticsData = {};
		// call method to update class string and array variables
		if (filterType === 'category') {
			this.updateSelectedCategory(filterItem);
			analyticsData = {
				filterCategory: filterType,
				filterName: this.selectedCategory.itemName.trim(),
				filterID: this.selectedCategory.itemKey,
			};
		} else {
			this.updateSelectedSubCategory(filterItem.itemKey, isSelected);
			const filterNames = this.selectedSubCategories.map((subcategory) => {
				const selector = `[data-subcategory-key='${subcategory}']`;
				return $(selector)
					.get(0)
					.innerText.trim();
			});
			analyticsData = {
				filterCategory: filterType,
				filterName: filterNames.join('|'),
				filterID: this.selectedSubCategories.join('|'),
			};
		}

		this.getResults(Actions.Replace, true);
		dispatchCustomEvent(this.$ctx, EventNames.FilterClick, analyticsData);
	};

	private parseResult(fragment: string): void {
		const $fragment = $(fragment);

		this.hasLoadMore = $fragment.data('has-load-more');
		this.currentPage = $fragment.data('current-page');
		this.resultCount = $fragment.data('result-count');
	}

	private setResultCount(resultCount: number): void {
		this.$resultCount.text(resultCount);
	}

	private setResultTitle(resultText: string): void {
		this.$resultTitle.text(resultText);
	}

	private showErrorMessage(): void {
		this.$ctx.addClass(States.Error);
	}

	private toggleLoadingState(newState: boolean): void {
		if (newState) {
			this.$loader.addClass(States.LoaderVisible);
			this.$loadMore.attr('disabled', 'disabled');
		} else {
			this.$loader.removeClass(States.LoaderVisible);
			this.$loadMore.removeAttr('disabled');
		}
	}

	private toggleLoadMore(newState: boolean): void {
		if (newState) {
			this.$loadMore.slideDown();
		} else {
			this.$loadMore.slideUp();
		}
	}

	private togglePurposeHubTeaser(): void {
		/* the manual teaser row ('show on top') has to be hidden if:
		 * - selected category is different from default category
		 * - one or more subcategory is selected
		 * - if subcategory key is different from 'all' (this.subCategoryAllKeyName)
		 */

		// by default show manual teaser row
		let showManualTeaserRow = true;
		// set conditions for hiding
		const selectedCategoryIsDifferentFromDefault = this.selectedCategory.itemKey !== this.defaultCategory.itemKey;
		const subCategoryIsSelectedButNotWithAllKey =
			this.selectedSubCategories.length > 0 &&
			this.selectedSubCategories.indexOf(this.subCategoryAllKeyName) === -1;
		// pack them as array
		const hideConditions = [selectedCategoryIsDifferentFromDefault, subCategoryIsSelectedButNotWithAllKey];

		// check array via indexOf if any of the hiding conditions is true
		if (hideConditions.indexOf(true) > -1) {
			showManualTeaserRow = false;
		}
		if (showManualTeaserRow) {
			this.$purposeHubTeaser.slideDown();
		} else {
			this.$purposeHubTeaser.slideUp();
		}
	}

	private updateHistory(title: string, url: string): void {
		if (window.history) {
			// update uri and page title
			history.pushState(
				{
					selectedCategory: this.selectedCategory,
					selectedSubCategories: this.selectedSubCategories,
					resultCount: this.resultCount,
					currentPage: this.currentPage,
				},
				title,
				url
			);
		}

		// set browser tab title even if it's not supported by pushState
		$(document).prop('title', title);
	}

	private updateSelectedCategory(filterItem: { itemKey: string; itemName: string }): void {
		this.selectedCategory = filterItem;
		this.selectedSubCategories = [this.subCategoryAllKeyName];
		this.currentPage = 0;
		this.listFilter.setFilterState(this.selectedCategory.itemKey, [this.subCategoryAllKeyName]);
	}

	private updateSelectedSubCategory(filterValue: string, isSelected: boolean): void {
		// with each (de)selection special treatment for 'all' subcat filter must be done
		if (filterValue === this.subCategoryAllKeyName) {
			this.selectedSubCategories = [this.subCategoryAllKeyName];
		} else {
			// logic for ACTIVATING a filter
			if (isSelected) {
				// make sure 'all' subcat filter is removed from selectedSubCategories
				const filteredSelectedSubCategories = this.selectedSubCategories.filter(
					(innerfilterValue, index, arr) => {
						return innerfilterValue !== this.subCategoryAllKeyName;
					}
				);
				this.selectedSubCategories = filteredSelectedSubCategories;
				this.selectedSubCategories.push(filterValue);
			} else {
				// logic for DEACTIVATING a filter
				// make sure deselected filterValue is removed from selectedSubCategories
				// but if selectedSubCategories is empty, put 'all' back in
				if (this.selectedSubCategories.indexOf(filterValue) > -1) {
					const categoryIndex = this.selectedSubCategories.indexOf(filterValue);
					this.selectedSubCategories.splice(categoryIndex, 1);
				}
				if (this.selectedSubCategories.length === 0) {
					this.selectedSubCategories = [this.subCategoryAllKeyName];
				}
			}
		}

		this.currentPage = 0;
	}

	private wait(delay: number) {
		return new Promise((receive) => setTimeout(receive, delay));
	}
}
